import type { BigIntish } from '@morpho-org/blue-sdk'

import { capitalize, startCase } from 'lodash-es'

import {
  getTimelockCriticality as getTimelockCriticalityBase,
  sortPendingTimelocks as sortPendingTimelocksBase,
  type TimelockCriticality,
  type TimelockFunctionConfig
} from '@/components/VaultPage/helpers/timelock'

export type { TimelockCriticality, TimelockFunctionConfig }

/**
 * Vault V2 specific timelock function configurations.
 */
export const TIMELOCK_FUNCTION_CONFIG: Record<string, TimelockFunctionConfig> = {
  AddAdapter: {
    label: 'Add adapter',
    criticality: 'high'
  },
  RemoveAdapter: {
    label: 'Remove adapter',
    criticality: 'high'
  }
}

/**
 * Custom labels for decoded function names displayed in timelock change components.
 * Keys are the decoded function names, values are the custom labels to display.
 */
export const DECODED_FUNCTION_NAME_LABELS: Record<string, string> = {
  setIsAllocator: 'Add / Remove allocator',
  increaseTimelock: 'Increase timelock duration'
}

/**
 * Gets the display label for a decoded function name.
 * Returns the custom label if defined, otherwise formats the function name.
 */
export const getDecodedFunctionNameLabel = (decodedFunctionName: string): string => {
  return (
    DECODED_FUNCTION_NAME_LABELS[decodedFunctionName] ?? capitalize(startCase(decodedFunctionName))
  )
}

/**
 * Gets the criticality level for a given timelock function name.
 * Returns "normal" as default if the function name is not found.
 */
export const getTimelockCriticality = (functionName: string): TimelockCriticality => {
  return getTimelockCriticalityBase(functionName, TIMELOCK_FUNCTION_CONFIG)
}

/**
 * Sorts pending timelocks by criticality (high first) then by validAt (ascending).
 */
export const sortPendingTimelocks = <T extends { functionName: string; validAt: BigIntish }>(
  items: T[]
): T[] => {
  return sortPendingTimelocksBase(items, TIMELOCK_FUNCTION_CONFIG)
}

/**
 * Gets the configuration (label and tooltip) for a given timelock function name.
 * Returns a default configuration if the function name is not found.
 */
export const getTimelockFunctionConfig = ({
  functionName
}: {
  functionName: string
}): TimelockFunctionConfig => {
  return (
    TIMELOCK_FUNCTION_CONFIG[functionName] ?? {
      label: capitalize(startCase(functionName)),
      criticality: 'normal'
    }
  )
}
